/* global SERVER_BASE_URL */
/* global IDENTITY_SERVICE_URL */
/* global IDENTITY_CLIENT_ID */

/**
 * Fallback auth config when the identity service config endpoint
 * is unreachable or returns an error.
 */
export const FALLBACK_AUTH_CONFIG = {
  use_frame: false,
}

/**
 * Unwrap a proxy response, throwing on failure.
 * @param {Object} result - { success, status, statusText, data, error }
 * @returns {*} result.data on success
 * @throws {Error} on failure
 */
function unwrapProxyResponse(result) {
  if (!result.success) {
    const message = result.error || result.data?.detail || result.data?.error || `Proxy request failed with status ${result.status}`
    const error = new Error(message)
    error.status = result.status
    error.data = result.data
    throw error
  }
  return result.data
}

/**
 * Appraiser backend endpoints — the legacy auth surface.
 */
export const appraiserBackendEndpoints = {
  LOGIN: async (username, password) => {
    const response = await fetch(`${SERVER_BASE_URL}/api/auth`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    })
    const data = await response.json()
    if (!response.ok) {
      const error = new Error(data.message || 'Login failed')
      error.status = response.status
      error.data = data
      throw error
    }
    return data
  },

  LOGOUT: async (currentBearerToken) => {
    const headers = {}
    if (currentBearerToken) {
      headers.Authorization = `Bearer ${currentBearerToken}`
    }
    await fetch(`${SERVER_BASE_URL}/api/auth/logout`, {
      method: 'POST',
      headers,
    }).catch(() => {})
  },
}

/**
 * Identity service endpoints — routed through the iframe auth proxy.
 * Each operation corresponds to an endpoint on the identity service
 * that the iframe's postMessage handler forwards to.
 */
export const identityServiceEndpoints = {
  LOGIN: async (proxy, username, password) => {
    const result = await proxy.operation('auth:login', {
      body: { username, password, client_id: IDENTITY_CLIENT_ID },
    })
    return unwrapProxyResponse(result)
  },

  LOGIN_CODE: async (proxy, code) => {
    const result = await proxy.operation('auth:login-code', {
      body: { code, client_id: IDENTITY_CLIENT_ID },
    })
    return unwrapProxyResponse(result)
  },

  LOGIN_TOKEN: async (proxy, token) => {
    const result = await proxy.operation('auth:login-token', {
      body: { token, client_id: IDENTITY_CLIENT_ID },
    })
    return unwrapProxyResponse(result)
  },

  STATUS: async (proxy, currentBearerToken) => {
    const headers = {}
    if (currentBearerToken) {
      headers.Authorization = `Bearer ${currentBearerToken}`
    }
    const result = await proxy.operation('auth:status', { headers })
    return unwrapProxyResponse(result)
  },

  TOKEN: async (proxy) => {
    const result = await proxy.operation('auth:token')
    return unwrapProxyResponse(result)
  },

  LOGOUT: async (proxy, currentBearerToken) => {
    const headers = {}
    if (currentBearerToken) {
      headers.Authorization = `Bearer ${currentBearerToken}`
    }
    const result = await proxy.operation('auth:logout', { headers })
    unwrapProxyResponse(result)
  },
}

/**
 * Auth config endpoint — fetches configuration from the identity service.
 * Falls back gracefully to legacy auth if unreachable.
 */
export const authConfigEndpoints = {
  GET_CONFIG: async () => {
    try {
      const response = await fetch(
        `${IDENTITY_SERVICE_URL}/vendor/auth/config`,
        { signal: AbortSignal.timeout(2500) },
      )
      if (!response.ok) {
        return FALLBACK_AUTH_CONFIG
      }
      return await response.json()
    } catch (error) {
      return FALLBACK_AUTH_CONFIG
    }
  },
}
